import { BaseSensor, SensorHealth } from '../../types/sensor';
import { DeviceInfo } from '../../types/session';

export interface CameraConfig {
  deviceId?: string;
  idealWidth: number;   // e.g. 1280 or 640
  idealHeight: number;  // e.g. 720 or 480
  idealFrameRate: number; // e.g. 30
}

export class CameraCaptureSensor implements BaseSensor<CameraConfig, ImageBitmap> {
  readonly id: string;
  readonly name: string;

  private config: CameraConfig = {
    idealWidth: 640,
    idealHeight: 480,
    idealFrameRate: 30,
  };

  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private animFrameId: number | null = null;
  private isRunning = false;

  private callbacks: Array<(bitmap: ImageBitmap) => void> = [];

  // Diagnostics & Stats
  private status: SensorHealth['status'] = 'idle';
  private lastEmitMs = 0;
  private droppedCount = 0;
  private errorCount = 0;
  private lastError?: string;
  private qualityFlags: Set<string> = new Set();

  private actualWidth = 0;
  private actualHeight = 0;
  private actualFps = 0;
  private frameCount = 0;
  private fpsWindowStart = 0;

  constructor(id = 'camera.capture.a', name = 'Camera A') {
    this.id = id;
    this.name = name;
  }

  describe() {
    return {
      id: this.id,
      name: this.name,
      version: '1.0.0',
      description: 'MediaDevices video stream capture software sensor',
    };
  }

  configure(config: Partial<CameraConfig>): void {
    this.config = { ...this.config, ...config };
  }

  static async enumerateVideoDevices(): Promise<DeviceInfo[]> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error('MediaDevices API 不受当前浏览器支持，或未在 HTTPS / localhost 环境下运行。');
    }

    let devices = await navigator.mediaDevices.enumerateDevices();
    let videoInputs = devices.filter((d) => d.kind === 'videoinput');

    // If device labels are blank (due to missing initial media permission), request quick permission to reveal full names (e.g. iPhone Continuity Camera, FaceTime Camera)
    if (videoInputs.length > 0 && videoInputs.some((d) => !d.label)) {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach((track) => track.stop());
        devices = await navigator.mediaDevices.enumerateDevices();
        videoInputs = devices.filter((d) => d.kind === 'videoinput');
      } catch (e) {
        console.warn('Unable to request temp camera permission for labels:', e);
      }
    }

    return videoInputs.map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label || `摄像头 ${i + 1} (${d.deviceId.slice(0, 8)})`,
      kind: 'videoinput',
    }));
  }

  async start(): Promise<void> {
    if (this.isRunning) return;

    this.status = 'configuring';
    this.qualityFlags.clear();

    const constraints: MediaStreamConstraints = {
      video: {
        deviceId: this.config.deviceId ? { exact: this.config.deviceId } : undefined,
        width: { ideal: this.config.idealWidth },
        height: { ideal: this.config.idealHeight },
        frameRate: { ideal: this.config.idealFrameRate },
      },
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      const track = this.stream.getVideoTracks()[0];
      const settings = track.getSettings();

      this.actualWidth = settings.width || this.config.idealWidth;
      this.actualHeight = settings.height || this.config.idealHeight;

      // Hidden video element to consume stream
      this.videoElement = document.createElement('video');
      this.videoElement.autoplay = true;
      this.videoElement.playsInline = true;
      this.videoElement.muted = true;
      this.videoElement.srcObject = this.stream;

      await this.videoElement.play();

      this.isRunning = true;
      this.status = 'running';
      this.fpsWindowStart = performance.now();
      this.frameCount = 0;

      this.loop();
    } catch (err: any) {
      this.status = 'error';
      this.errorCount++;
      this.lastError = err.message || String(err);
      this.qualityFlags.add('camera-unavailable');
      throw new Error(`摄像头启动失败 (${this.name}): ${this.lastError}`);
    }
  }

  private loop = () => {
    if (!this.isRunning || !this.videoElement) return;

    if (this.videoElement.readyState >= 2) { // HAVE_CURRENT_DATA
      const now = performance.now();
      this.lastEmitMs = now;
      this.frameCount++;

      if (now - this.fpsWindowStart >= 1000) {
        this.actualFps = Math.round((this.frameCount * 1000) / (now - this.fpsWindowStart));
        this.frameCount = 0;
        this.fpsWindowStart = now;
      }

      createImageBitmap(this.videoElement)
        .then((bitmap) => {
          for (const cb of this.callbacks) {
            cb(bitmap);
          }
        })
        .catch(() => {
          this.droppedCount++;
        });
    } else {
      this.droppedCount++;
    }

    this.animFrameId = requestAnimationFrame(this.loop);
  };

  async stop(): Promise<void> {
    this.isRunning = false;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    this.status = 'stopped' as any;
  }

  health(): SensorHealth {
    return {
      status: this.status,
      lastEmitMonotonicMs: this.lastEmitMs,
      droppedCount: this.droppedCount,
      errorCount: this.errorCount,
      lastErrorMessage: this.lastError,
      qualityFlags: Array.from(this.qualityFlags),
    };
  }

  getDeviceInfo(): DeviceInfo {
    return {
      deviceId: this.config.deviceId || 'default',
      label: this.name,
      kind: 'videoinput',
      requestedWidth: this.config.idealWidth,
      requestedHeight: this.config.idealHeight,
      actualWidth: this.actualWidth,
      actualHeight: this.actualHeight,
      actualFrameRate: this.actualFps,
    };
  }

  onData(callback: (bitmap: ImageBitmap) => void): void {
    this.callbacks.push(callback);
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }
}
